import React, { useState, useEffect } from 'react';
import documentApi from '../services/documentApi';
import './DocumentList.css';

const DocumentList = ({
  transactionType,
  transactionId,
  onDocumentDeleted,
  showUploadButton = false,
  onUploadClick
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    if (transactionType && transactionId) {
      fetchDocuments();
    }
  }, [transactionType, transactionId]);

  const fetchDocuments = async () => {
    if (!transactionType || !transactionId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const docs = await documentApi.getDocuments(transactionType, transactionId);
      setDocuments(docs);
    } catch (err) {
      setError('Failed to load documents');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      await documentApi.downloadAndSave(document.id, document.file_name);
    } catch (err) {
      alert('Failed to download document: ' + (err.message || 'Unknown error'));
      console.error('Error downloading document:', err);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeletingId(documentId);
    try {
      await documentApi.deleteDocument(documentId);
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      if (onDocumentDeleted) {
        onDocumentDeleted(documentId);
      }
    } catch (err) {
      alert('Failed to delete document: ' + (err.response?.data?.error || err.message || 'Unknown error'));
      console.error('Error deleting document:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditStart = (document) => {
    setEditingId(document.id);
    setEditDescription(document.description || '');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditDescription('');
  };

  const handleEditSave = async (documentId) => {
    try {
      const updated = await documentApi.updateDocument(documentId, {
        description: editDescription
      });
      setDocuments((prev) =>
        prev.map((doc) => (doc.id === documentId ? updated : doc))
      );
      setEditingId(null);
      setEditDescription('');
    } catch (err) {
      alert('Failed to update document: ' + (err.response?.data?.error || err.message || 'Unknown error'));
      console.error('Error updating document:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getFileIcon = (mimeType, fileName) => {
    if (mimeType?.includes('image')) {
      return '🖼️';
    } else if (mimeType === 'application/pdf') {
      return '📄';
    } else if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) {
      return '📊';
    }
    // Fallback based on extension
    const ext = '.' + fileName.split('.').pop().toLowerCase();
    if (['.jpg', '.jpeg', '.png'].includes(ext)) {
      return '🖼️';
    } else if (ext === '.pdf') {
      return '📄';
    } else if (['.xls', '.xlsx'].includes(ext)) {
      return '📊';
    }
    return '📎';
  };

  if (loading) {
    return (
      <div className="document-list-container">
        <div className="document-list-loading">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="document-list-container">
      <div className="document-list-header">
        <h4>Documents ({documents.length})</h4>
        {showUploadButton && onUploadClick && (
          <button
            type="button"
            className="document-list-upload-btn"
            onClick={onUploadClick}
          >
            + Upload Document
          </button>
        )}
      </div>

      {error && (
        <div className="document-list-error">{error}</div>
      )}

      {documents.length === 0 ? (
        <div className="document-list-empty">
          <p>No documents attached to this transaction.</p>
          {showUploadButton && onUploadClick && (
            <button
              type="button"
              className="document-list-upload-btn"
              onClick={onUploadClick}
            >
              Upload First Document
            </button>
          )}
        </div>
      ) : (
        <div className="document-list-items">
          {documents.map((document) => (
            <div key={document.id} className="document-list-item">
              <div className="document-list-item-icon">
                {getFileIcon(document.mime_type, document.file_name)}
              </div>
              <div className="document-list-item-content">
                <div className="document-list-item-name">{document.file_name}</div>
                {editingId === document.id ? (
                  <div className="document-list-item-edit">
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Add description..."
                      rows={2}
                    />
                    <div className="document-list-item-edit-actions">
                      <button
                        type="button"
                        onClick={() => handleEditSave(document.id)}
                        className="document-list-btn-save"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={handleEditCancel}
                        className="document-list-btn-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {document.description && (
                      <div className="document-list-item-description">
                        {document.description}
                      </div>
                    )}
                    <div className="document-list-item-meta">
                      <span>{formatFileSize(document.file_size)}</span>
                      <span>•</span>
                      <span>{formatDate(document.created_at)}</span>
                      {document.uploaded_by_username && (
                        <>
                          <span>•</span>
                          <span>Uploaded by {document.uploaded_by_username}</span>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="document-list-item-actions">
                {editingId !== document.id && (
                  <>
                    <button
                      type="button"
                      className="document-list-btn-edit"
                      onClick={() => handleEditStart(document)}
                      title="Edit description"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="document-list-btn-download"
                      onClick={() => handleDownload(document)}
                      title="Download"
                    >
                      ⬇️
                    </button>
                    <button
                      type="button"
                      className="document-list-btn-delete"
                      onClick={() => handleDelete(document.id)}
                      disabled={deletingId === document.id}
                      title="Delete"
                    >
                      {deletingId === document.id ? '⏳' : '🗑️'}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentList;
